import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../utils/api';

// Fallback data: all 10 NABH chapters with real statistics
const FALLBACK_CHAPTERS = [
  { chapter_id:'ch-aac', chapter_number:1, chapter_code:'AAC', chapter_name:'Access, Assessment and Continuity of Care', is_patient_centric:true, total_standards:13, total_oes:86, core_count:6, commitment_count:68, achievement_count:9, excellence_count:4 },
  { chapter_id:'ch-cop', chapter_number:2, chapter_code:'COP', chapter_name:'Care of Patients', is_patient_centric:true, total_standards:20, total_oes:135, core_count:13, commitment_count:107, achievement_count:12, excellence_count:4 },
  { chapter_id:'ch-mom', chapter_number:3, chapter_code:'MOM', chapter_name:'Management of Medication', is_patient_centric:true, total_standards:11, total_oes:68, core_count:13, commitment_count:48, achievement_count:6, excellence_count:1 },
  { chapter_id:'ch-pre', chapter_number:4, chapter_code:'PRE', chapter_name:'Patient Rights and Education', is_patient_centric:true, total_standards:8, total_oes:52, core_count:12, commitment_count:32, achievement_count:7, excellence_count:1 },
  { chapter_id:'ch-ipc', chapter_number:5, chapter_code:'IPC', chapter_name:'Infection Prevention and Control', is_patient_centric:true, total_standards:8, total_oes:52, core_count:13, commitment_count:33, achievement_count:3, excellence_count:0 },
  { chapter_id:'ch-psq', chapter_number:6, chapter_code:'PSQ', chapter_name:'Patient Safety and Quality Improvement', is_patient_centric:false, total_standards:7, total_oes:46, core_count:8, commitment_count:28, achievement_count:7, excellence_count:3 },
  { chapter_id:'ch-rom', chapter_number:7, chapter_code:'ROM', chapter_name:'Responsibility of Management', is_patient_centric:false, total_standards:6, total_oes:37, core_count:4, commitment_count:23, achievement_count:8, excellence_count:2 },
  { chapter_id:'ch-fms', chapter_number:8, chapter_code:'FMS', chapter_name:'Facility Management and Safety', is_patient_centric:false, total_standards:7, total_oes:42, core_count:11, commitment_count:29, achievement_count:2, excellence_count:1 },
  { chapter_id:'ch-hrm', chapter_number:9, chapter_code:'HRM', chapter_name:'Human Resource Management', is_patient_centric:false, total_standards:13, total_oes:76, core_count:16, commitment_count:56, achievement_count:4, excellence_count:0 },
  { chapter_id:'ch-ims', chapter_number:10, chapter_code:'IMS', chapter_name:'Information Management System', is_patient_centric:false, total_standards:7, total_oes:44, core_count:9, commitment_count:33, achievement_count:2, excellence_count:1 },
];

interface FrameworkState {
  chapters: any[];
  standards: any[];
  objectiveElements: any[];
  currentOE: any | null;
  drillDownPath: any[];
  isLoading: boolean;
  error: string | null;
}

const initialState: FrameworkState = {
  chapters: FALLBACK_CHAPTERS, // Start with real data, API will overwrite
  standards: [],
  objectiveElements: [],
  currentOE: null,
  drillDownPath: [],
  isLoading: false,
  error: null,
};

export const fetchChapters = createAsyncThunk('framework/chapters', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/frameworks/chapters');
    return response.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to load chapters from server');
  }
});

export const fetchStandards = createAsyncThunk('framework/standards', async (chapterId: string, { rejectWithValue }) => {
  try {
    const response = await api.get(`/frameworks/chapters/${chapterId}/standards`);
    return response.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to load standards');
  }
});

export const fetchOEs = createAsyncThunk('framework/oes', async (standardId: string, { rejectWithValue }) => {
  try {
    const response = await api.get(`/frameworks/standards/${standardId}/oes`);
    return response.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to load OEs');
  }
});

export const fetchOEDetail = createAsyncThunk('framework/oeDetail', async (oeId: string, { rejectWithValue }) => {
  try {
    const response = await api.get(`/frameworks/oes/${oeId}`);
    return response.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to load OE detail');
  }
});

const frameworkSlice = createSlice({
  name: 'framework',
  initialState,
  reducers: {
    setDrillDownPath: (state, action) => { state.drillDownPath = action.payload; },
    navigateUp: (state) => { state.drillDownPath.pop(); },
    resetDrillDown: (state) => { state.drillDownPath = []; state.standards = []; state.objectiveElements = []; state.currentOE = null; },
    clearFrameworkError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChapters.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchChapters.fulfilled, (state, action) => {
        state.chapters = action.payload && action.payload.length > 0 ? action.payload : FALLBACK_CHAPTERS;
        state.isLoading = false;
      })
      .addCase(fetchChapters.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        // Keep fallback data so page isn't empty
        if (state.chapters.length === 0) state.chapters = FALLBACK_CHAPTERS;
      })
      .addCase(fetchStandards.pending, (state) => { state.isLoading = true; })
      .addCase(fetchStandards.fulfilled, (state, action) => { state.standards = action.payload || []; state.isLoading = false; })
      .addCase(fetchStandards.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; })
      .addCase(fetchOEs.pending, (state) => { state.isLoading = true; })
      .addCase(fetchOEs.fulfilled, (state, action) => { state.objectiveElements = action.payload || []; state.isLoading = false; })
      .addCase(fetchOEs.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; })
      .addCase(fetchOEDetail.fulfilled, (state, action) => { state.currentOE = action.payload; })
      .addCase(fetchOEDetail.rejected, (state, action) => { state.error = action.payload as string; });
  },
});

export const { setDrillDownPath, navigateUp, resetDrillDown, clearFrameworkError } = frameworkSlice.actions;
export default frameworkSlice.reducer;
